import { Fragment, ReactNode } from "react";
import Tooltip from "@/common/Tooltip";
import clsx from "clsx";

/**
 * A row of mutually exclusive choices, for the handful of cases where the
 * options read better side by side than folded into a dropdown.
 *
 * `size="sm"` is for a control that sits inside a row of a list, where the
 * default would outweigh the thing it describes.
 *
 * `tone` colours the chosen option by what it stands for, for choices that are
 * steps of one scale: the further up, the more of the accent.
 */
export type SegmentedTone = "neutral" | "soft" | "solid";

const TONES: Record<SegmentedTone, string> = {
    // No track behind the options, so the chosen one is a tint rather than
    // a white tile: on a white card a white tile would not show.
    neutral: "bg-zinc-950/5 text-zinc-950 dark:bg-white/10 dark:text-white",
    soft: "bg-indigo-100 text-indigo-700 dark:bg-indigo-400/20 dark:text-indigo-200",
    solid: "bg-indigo-600 text-white shadow-sm dark:bg-white dark:text-plum-900",
};

export const Segmented = <T extends string>({
    options,
    value,
    onChange,
    label,
    renderOption,
    size = "md",
    disabled = false,
    tone,
    tooltip,
    optionLabel,
}: {
    options: T[];
    value: T;
    onChange: (next: T) => void;
    label: string;
    renderOption: (option: T) => ReactNode;
    size?: "md" | "sm";
    disabled?: boolean;
    tone?: (option: T) => SegmentedTone;
    /**
     * What an option means, on hover. For options drawn as an icon alone,
     * which also need a name: pass it as the button's `aria-label` through
     * `optionLabel`.
     */
    tooltip?: (option: T) => ReactNode;
    /** The accessible name of an option whose content is not text. */
    optionLabel?: (option: T) => string;
}) => (
    <div
        role="radiogroup"
        aria-label={label}
        className={clsx(
            // As wide as its options and no wider: in a stacked form the
            // column would otherwise stretch the outline across the dialog.
            "inline-flex w-fit rounded-lg ring-1 ring-zinc-950/10 dark:ring-white/10",
            size === "sm" ? "p-0.5" : "p-1",
            disabled && "opacity-50",
        )}
    >
        {options.map((option, index) => {
            const button = (
                <button
                    type="button"
                    role="radio"
                    aria-checked={option === value}
                    aria-label={optionLabel?.(option)}
                    disabled={disabled}
                    // One tab stop for the group and arrows within it, as a
                    // group of radios behaves.
                    tabIndex={option === value ? 0 : -1}
                    onKeyDown={(event) => {
                        const step =
                            event.key === "ArrowRight" ||
                            event.key === "ArrowDown"
                                ? 1
                                : event.key === "ArrowLeft" ||
                                    event.key === "ArrowUp"
                                  ? -1
                                  : 0;
                        if (step === 0) return;
                        event.preventDefault();
                        const next =
                            options[
                                (options.indexOf(option) +
                                    step +
                                    options.length) %
                                    options.length
                            ]!;
                        onChange(next);
                        const buttons =
                            event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                                "[role=radio]",
                            );
                        buttons?.[options.indexOf(next)]?.focus();
                    }}
                    onClick={() => onChange(option)}
                    className={clsx(
                        "inline-flex items-center gap-2 rounded-md transition-colors",
                        size === "sm"
                            ? "px-2.5 py-0.5 text-xs font-medium"
                            : optionLabel
                              ? // An icon needs less room either side than
                                // a word does.
                                "px-2 py-1"
                              : "px-4 py-1 text-sm font-semibold",
                        option === value
                            ? TONES[tone?.(option) ?? "neutral"]
                            : "text-zinc-500 hover:bg-zinc-950/[2.5%] hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/5 dark:hover:text-white",
                        disabled && "cursor-default",
                    )}
                >
                    {renderOption(option)}
                </button>
            );
            return (
                <Fragment key={option}>
                    {/* Icons sit close together, so a faint dashed rule
                        keeps each one its own choice. Not beside the chosen
                        one: its tint is already an edge, and a rule against
                        it reads as cramped. The rule keeps its room either
                        way, so nothing shifts as the choice moves. */}
                    {optionLabel && index > 0 && (
                        <span
                            aria-hidden={"true"}
                            className={clsx(
                                "mx-0.5 my-1.5 border-l border-dashed transition-colors",
                                option === value || options[index - 1] === value
                                    ? "border-transparent"
                                    : "border-zinc-200 dark:border-white/10",
                            )}
                        />
                    )}
                    {tooltip ? (
                        <Tooltip asChild content={tooltip(option)}>
                            {button}
                        </Tooltip>
                    ) : (
                        button
                    )}
                </Fragment>
            );
        })}
    </div>
);
